import { z } from "zod";
import {
  approvalStatusSchema,
  documentNormalizationStatusSchema,
  policySourceSchema,
  processingStatusSchema,
} from "../enums.js";
import { isoDate, optionalIsoDate, validateTargeting } from "./common.js";

export const policyVersionSchema = z.object({
  id: z.string(),
  version: z.number().int().positive(),
  approvalStatus: approvalStatusSchema,
  processingStatus: processingStatusSchema,
  /**
   * Why the last status poll reported a non-terminal or failed state, verbatim
   * from VaultSage. A `FAILED` status with no reason is the case that used to
   * cost a Cloud Logging dig, so this is populated on every poll and cleared
   * once the document indexes.
   */
  processingError: z.string().nullable(),
  /** When the status was last polled upstream — null before the first poll. */
  processingCheckedAt: optionalIsoDate,
  /**
   * The version was registered with "apply automatically" and is waiting for
   * its document to finish indexing before it replaces the active version.
   */
  autoActivateOnReady: z.boolean(),
  vaultsageFileId: z.string().nullable(),
  fileName: z.string().nullable(),
  sourceMimeType: z.string().nullable(),
  normalizationStatus: documentNormalizationStatusSchema,
  normalizationErrorCode: z.string().nullable(),
  normalizedProcessingStatus: processingStatusSchema.nullable(),
  normalizedProcessingCheckedAt: optionalIsoDate,
  effectiveDate: optionalIsoDate,
  notes: z.string().nullable(),
  appliesToAllChannels: z.boolean(),
  appliesToAllCategories: z.boolean(),
  channels: z.array(
    z.object({
      channelId: z.string(),
      code: z.string(),
      name: z.string(),
      note: z.string().nullable(),
    }),
  ),
  categories: z.array(
    z.object({
      categoryId: z.string(),
      code: z.string(),
      name: z.string(),
      parentName: z.string().nullable(),
    }),
  ),
  createdAt: isoDate,
  approvedAt: optionalIsoDate,
});
export type PolicyVersion = z.infer<typeof policyVersionSchema>;

/**
 * Where a policy sits in its lifecycle. ARCHIVED and DELETED policies keep
 * their versions, scope mappings, and audit history; they simply stop taking
 * part in new compliance checks.
 */
export const policyLifecycleStatusSchema = z.enum(["ACTIVE", "ARCHIVED", "DELETED"]);
export type PolicyLifecycleStatus = z.infer<typeof policyLifecycleStatusSchema>;

export const policySchema = z.object({
  id: z.string(),
  title: z.string(),
  source: policySourceSchema,
  authority: z.string().nullable(),
  activeVersionId: z.string().nullable(),
  lifecycleStatus: policyLifecycleStatusSchema,
  archivedAt: optionalIsoDate,
  deletedAt: optionalIsoDate,
  versions: z.array(policyVersionSchema),
  createdAt: isoDate,
});
export type Policy = z.infer<typeof policySchema>;

/**
 * Everything a list row needs at a glance — the latest version's status,
 * without the channel/category scope mappings a list view never renders.
 * The full `Policy` (all versions + scope) is fetched on demand via
 * `GET /policies/:id` when the user opens the version history.
 */
export const policyVersionSummarySchema = z.object({
  id: z.string(),
  version: z.number().int().positive(),
  approvalStatus: approvalStatusSchema,
  processingStatus: processingStatusSchema,
  fileName: z.string().nullable(),
  normalizationStatus: documentNormalizationStatusSchema,
  effectiveDate: optionalIsoDate,
  createdAt: isoDate,
  approvedAt: optionalIsoDate,
});
export type PolicyVersionSummary = z.infer<typeof policyVersionSummarySchema>;

export const policyListItemSchema = z.object({
  id: z.string(),
  title: z.string(),
  source: policySourceSchema,
  authority: z.string().nullable(),
  activeVersionId: z.string().nullable(),
  lifecycleStatus: policyLifecycleStatusSchema,
  archivedAt: optionalIsoDate,
  deletedAt: optionalIsoDate,
  createdAt: isoDate,
  versionCount: z.number().int().nonnegative(),
  latestVersion: policyVersionSummarySchema.nullable(),
});
export type PolicyListItem = z.infer<typeof policyListItemSchema>;

const policyChannelInputSchema = z.object({
  channelId: z.string().min(1),
  note: z.string().trim().max(300).optional(),
});

const policyTargetingFields = {
  appliesToAllChannels: z.boolean().default(true),
  appliesToAllCategories: z.boolean().default(true),
  channels: z.array(policyChannelInputSchema).max(100).optional(),
  categoryIds: z.array(z.string().min(1)).max(100).optional(),
};

export const createPolicySchema = z
  .object({
    title: z.string().min(1).max(300),
    source: policySourceSchema,
    authority: z.string().max(300).optional(),
    effectiveDate: isoDate.optional(),
    notes: z.string().max(4000).optional(),
    ...policyTargetingFields,
  })
  .superRefine(validateTargeting);
export type CreatePolicyInput = z.infer<typeof createPolicySchema>;

export const updatePolicySchema = z.object({
  title: z.string().trim().min(1).max(300),
});
export type UpdatePolicyInput = z.infer<typeof updatePolicySchema>;

export const createPolicyVersionSchema = z
  .object({
    effectiveDate: isoDate.optional(),
    notes: z.string().max(4000).optional(),
    /**
     * Replace the currently active version with this one as soon as it is
     * usable. Defaults to true: registering a newer version of a policy is
     * normally an intent to enforce it, and leaving it inactive was a silent
     * way to have an out-of-date policy keep applying.
     */
    activateWhenReady: z.boolean().default(true),
    ...policyTargetingFields,
  })
  .superRefine(validateTargeting);
export type CreatePolicyVersionInput = z.infer<typeof createPolicyVersionSchema>;
